/** Pure helper for the LayersPanel's drag-and-drop drop-target
 *  resolution. Given a flat layer list, the source / target
 *  layers, the edge of the target row the pointer hit, and the
 *  current collapsed-groups set, returns the `(parentId, index)`
 *  pair the caller should hand to `moveNode`. Lives in `lib/`
 *  so it can be unit-tested without spinning up the panel.
 *
 *  Drop semantics — preserved verbatim from the inline handler
 *  this replaced:
 *    - top-half of any row → insert BEFORE that row, at the
 *      row's parent
 *    - bottom-half of an EXPANDED group row → insert AS THE
 *      FIRST CHILD of that group (drop-into)
 *    - bottom-half of a COLLAPSED group row → insert AFTER the
 *      group at the group's parent
 *    - bottom-half of a leaf row → insert AFTER that row, at
 *      the row's parent
 *
 *  Same-parent detach-shift compensation: `moveNode` detaches
 *  the source first and then inserts. If the source was upstream
 *  in the same parent's child list, the parent loses one child
 *  before the insert, so any post-source destination index has
 *  to shift down by 1. Without this, dragging `a` onto the
 *  bottom of `b` in `[a, b, c]` lands as `[b, c, a]` instead of
 *  the intended `[b, a, c]`. Cross-parent moves don't need the
 *  shift because the source's old parent is different from the
 *  new parent. */

import type { Layer } from '../types';

export interface DropTarget {
  parentId: string | null;
  index: number;
}

export function computeDropTarget(
  layers: Layer[],
  sourceLayer: Layer,
  targetLayer: Layer,
  edge: 'top' | 'bottom',
  collapsedGroupIds: ReadonlySet<string>,
): DropTarget {
  const targetParent = targetLayer.parentId ?? null;
  const siblings =
    targetParent === null
      ? layers.filter((l) => l.parentId === null)
      : layers.filter((l) => l.parentId === targetParent);
  const rowIndexInSiblings = siblings.findIndex((l) => l.id === targetLayer.id);

  let parentId: string | null = targetParent;
  let index: number;
  if (edge === 'bottom' && targetLayer.type === 'group' && !collapsedGroupIds.has(targetLayer.id)) {
    parentId = targetLayer.id;
    index = 0;
  } else if (edge === 'top') {
    index = rowIndexInSiblings;
  } else {
    index = rowIndexInSiblings + 1;
  }

  const sourceParent = sourceLayer.parentId ?? null;
  if (sourceParent === parentId) {
    const sourceIndexInSiblings = siblings.findIndex((l) => l.id === sourceLayer.id);
    if (sourceIndexInSiblings >= 0 && sourceIndexInSiblings < index) {
      index -= 1;
    }
  }

  return { parentId, index };
}
